import { zIndex } from '@mathflat/common/style'
import { observer } from 'mobx-react'
import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router'

import { PORTAL_DEFAULT_ID } from '@/constant/ui/DOM'
import Notification from '@/contentComponents/global/Notification/Notification'
import Scenario from '@/contentComponents/global/Scenario/Scenario'
import { isPublicRoute } from '@/router'
import { isFullModalRoutes } from '@/router/utils'
import { scenarioService } from '@/service/scenario.service'
import type { DefaultProps } from '@/types'

// Portal을 렌더링할 element를 rootId를 받아 rootId별로 동적으로 생성하는 컴포넌트
// children에 Portal로 띄우고 싶은 컴포넌트를 넣는다.
export const Portal = ({ rootId = PORTAL_DEFAULT_ID, children }) => {
  const target = useRef(document.getElementById(rootId))

  useEffect(() => {
    return () => {
      if (target.current?.childNodes.length === 0) {
        target.current.remove()
        target.current = null
      }
    }
  }, [rootId])

  if (!target.current) {
    target.current = document.createElement('div')
    target.current.setAttribute('id', rootId)
    target.current.style.width = '100%'

    document.body.appendChild(target.current)
  }

  return createPortal(children, target.current)
}

// (전역, 화면 단위) portal로 띄우는 컴포넌트들을 여기서 관리한다.
const PortalContainer = () => {
  const { pathname } = useLocation()
  return (
    <>
      {/* default portal */}
      <Portal>
        <Notification />
        {!isPublicRoute(pathname) && !isFullModalRoutes(pathname) && scenarioService.enabled && (
          <Scenario />
        )}
      </Portal>
    </>
  )
}

Portal.Container = observer(PortalContainer)

export type PortalElementProps = {
  rect?: DOMRect
  isVisible: boolean
  offset: { x: number; y: number } // parentElement의 top-left 기준으로 offset
  fitWidth?: boolean
  safeHeight?: boolean
  minSafePaddingY?: number
  zIndex?: number
} & DefaultProps

// (전역X, 엘리먼트 단위) 전역이 아닌 parent 컴포넌트 위치 근처에 portal로서 띄워야하는 컴포넌트는 이것을 이용해 생성한다.
// ex. ScenarioPointer.tsx 참고
const PortalElement = ({
  rect,
  isVisible,
  style: _style,
  children,
  className,
  offset,
  fitWidth,
  safeHeight,
  minSafePaddingY = 24,
  zIndex: _zIndex,
}: PortalElementProps) => {
  if (!isVisible) return null

  const maxHeight = rect
    ? window.innerHeight - (rect.height + rect.top) - minSafePaddingY
    : undefined

  const style: React.CSSProperties = rect
    ? {
        position: 'absolute',
        zIndex: _zIndex || zIndex.Portal,
        top: rect.top + offset.y,
        left: rect.left + offset.x,
        ...(fitWidth && { width: rect.width }),
        ..._style,
        ...(safeHeight && { height: maxHeight }),
      }
    : {
        position: 'absolute',
        zIndex: _zIndex || zIndex.Portal,
        ..._style,
        ...(safeHeight && { height: maxHeight }),
      }

  return (
    <Portal>
      <div style={style} className={className}>
        {children}
      </div>
    </Portal>
  )
}

Portal.Element = observer(PortalElement)
